import { Amount } from 'src_common/common/amount/Amount';
import { SportOptionType } from './BetTickerBox.state';
import { BetType, RulesType } from './config';

export const matchStakeFactor = (
    sf: number | undefined,
    sfC: string | undefined,
    sfRule: number | undefined
): boolean => {
    const condition = sfC,
        sf2 = sfRule;

    if (sf2 === undefined || sf === undefined) {
        return false;
    }

    switch (condition) {
        case '>':
            return sf > sf2;
        case '<':
            return sf < sf2;
        case '=':
            return sf === sf2;
        case '>=':
            return sf >= sf2;
        case '<=':
            return sf <= sf2;
    }

    return false;
};

export const splitBet = (bet: BetType): BetType[] => {
    if (bet.body.bet.legs.length === 1) {
        return [bet];
    }

    const splitBets = bet.body.bet.legs.map((leg, index) => {
        const row: BetType = JSON.parse(JSON.stringify(bet));
        row.body.bet.legs = [leg];

        if (index > 0) {
            delete row.body.bet.placedAt;
            delete row.body.bet.stakeFactor;
            delete row.body.bet.eachWay;
            delete row.body.bet.stakePerLine;
            delete row.body.bet.totalStake;
            delete row.body.bet.payout;
            delete row.body.bet.maxBet;
            delete row.body.bet.channel;
            delete row.body.bet.eachWay;
        }
        return row;
    });

    return splitBets;
};

export const filterBets = (rules: RulesType | null, bets: BetType[]): BetType[] => {
    let filteredData = bets;

    if (rules?.freebet === true) {
        filteredData = filteredData.filter((bet) => bet.body.bet.freebet === true);
    }

    if (rules?.betType !== undefined) {
        filteredData = filteredData.filter((bet) => bet.body.bet.type === rules.betType);
    }

    if (rules?.channel !== undefined) {
        filteredData = filteredData.filter((bet) => bet.body.bet.channel === rules.channel);
    }

    if (rules?.betsAbove !== undefined) {
        filteredData = filteredData.filter((bet) => {
            if (rules.betsAbove === undefined) return true;
            else return (bet.body.bet.totalStake ?? 0) > parseFloat(rules.betsAbove.toFixed(2)) * 100;
        });
    }

    if (rules?.betsType !== undefined) {
        filteredData = filteredData.filter((bet) => rules.betsType?.includes(bet.body.bet.type));
    }

    if (rules?.stakeFactor !== undefined && rules.stakeFactorCondition !== undefined) {
        filteredData = filteredData.filter((bet) =>
            matchStakeFactor(bet.body.bet.stakeFactor, rules.stakeFactorCondition, rules.stakeFactor)
        );
    }

    if (rules?.sport !== undefined) {
        filteredData = filteredData.filter((bet) =>
            bet.body.bet.legs.find((leg) =>
                rules.sport?.find((y) => {
                    return Array.isArray(y.competitions)
                        ? y.id === leg.sport?.id && y.competitions.some((comp) => comp.id === leg.competition?.id)
                        : y.id === leg.sport?.id;
                })
            )
        );
    }

    if (rules?.cashOutBets === true) {
        filteredData = filteredData.filter((bet) => bet.body.bet.cashOut === true);
    }

    filteredData = filteredData.reverse().flatMap((row) => {
        return splitBet(row);
    });

    return filteredData;
};

export const createSportsLabel = (sports: SportOptionType[], rules: RulesType | null): string => {
    let sportsLabel;
    if (rules?.sport === undefined) {
        sportsLabel = 'All';
    } else {
        sportsLabel = rules.sport
            .map((s) => {
                let label = `${sports.find((x) => x.id === s.id)?.name} (`;

                if (s.competitions === true) {
                    label += 'All';
                } else if (
                    Array.isArray(s.competitions) &&
                    s.competitions.length === 1 &&
                    s.competitions[0]?.name !== undefined
                ) {
                    const competitionName = s.competitions[0].name;
                    label += competitionName;
                } else if (typeof s.competitions !== 'boolean') {
                    label += s.competitions.length;
                }
                return `${label})`;
            })
            .join(', ');
    }

    return sportsLabel;
};

export const createSettingsLabel = (rules: RulesType | null): string | undefined => {
    let settingsLabel = undefined;
    const settingsArray = [];
    if (rules?.betsAbove !== undefined) {
        settingsArray.push(`Bets above £${rules.betsAbove}`);
    }

    if (rules?.stakeFactor !== undefined) {
        settingsArray.push(`Stake factor ${rules.stakeFactorCondition} ${rules.stakeFactor}`);
    }

    if (settingsArray.length > 0) settingsLabel = settingsArray.join(', ');

    return settingsLabel;
};

export const convertAmount = (value: string | number): string | null => {
    if (typeof value === 'number') {
        return $appState.configState.precision.newFromOld(value).value;
    }

    if (typeof value === 'string') {
        return new Amount(value).value;
    }

    return null;
};
